import { createPortal } from "react-dom";
import RechargeModal from "../../Recharge/RechargeModal";
import { useClickOutside } from "../../../Hooks/useClickOutside";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import axios from "../../../../Utils/axiosInstance";

const PLATFORM_FEE = 69;

// ── Pay-to-view modal — gates offer details behind the platform fee ─────────
const PayToViewOfferModal = ({
  show,
  onClose,
  offer,
  wallet,
  user,
  onPaid,
}) => {
  const [animate, setAnimate] = useState(false);
  const [paying, setPaying] = useState(false);
  const [showRecharge, setShowRecharge] = useState(false);
  const [currentWallet, setCurrentWallet] = useState(wallet);

  useEffect(() => {
    if (show) {
      setTimeout(() => setAnimate(true), 10);
      setCurrentWallet(wallet);
    } else {
      setAnimate(false);
    }
  }, [show, wallet]);

  const handleClose = () => {
    setAnimate(false);
    setTimeout(onClose, 250);
  };

  const domNode = useClickOutside(() => {
    if (!showRecharge) handleClose();
  });

  if (!show || !offer) return null;

  const availableBalance = currentWallet?.availableBalance ?? 0;
  const hasEnoughBalance = availableBalance >= PLATFORM_FEE;
  const offerObj = offer?.offer || offer;
  const productVarietyObj = offer?.productVariety || offerObj?.productVariety || offer?.productHirarchy?.productVariety || offerObj?.productHirarchy?.productVariety;
  const quotationObj = offer?.quotation || offerObj?.quotation || offer?.quotationDetails || offerObj?.quotationDetails;

  const varietyName = productVarietyObj?.varietyName ?? offerObj?.productVariety?.varietyName ?? offer?.productVariety?.varietyName ?? "this product";
  const offerNumber = offerObj?.offerUniqueNumber || offer?.offerUniqueNumber;
  const rawGrade = productVarietyObj?.grade ?? offerObj?.productVariety?.grade ?? "";
  const gradeLabel = rawGrade
    ? /^grade\b/i.test(rawGrade.trim())
      ? rawGrade.trim().toUpperCase()
      : `GRADE ${rawGrade}`
    : "";
  const effPrice = offerObj?.pricing?.amount ?? offerObj?.pricing?.commissionAdjustedAmount ?? quotationObj?.pricing?.commissionAdjustedAmount ?? quotationObj?.pricing?.amount ?? 0;
  const priceAmount = effPrice;
  const cleanPriceType = (offerObj?.pricing?.priceType || "kg").replace(/per/gi, "").replace(/price/gi, "").trim().toLowerCase() || "kg";
  const priceType = cleanPriceType;

  const qtyInKg = offerObj?.quantity?.weightInNumber
    ? offerObj.quantity.weightIn?.toLowerCase() === "ton"
      ? offerObj.quantity.weightInNumber * 1000
      : offerObj.quantity.weightIn?.toLowerCase() === "quintal"
        ? offerObj.quantity.weightInNumber * 100
        : offerObj.quantity.weightInNumber
    : (quotationObj?.quantity?.volume ?? 0);

  let totalDealValue = (qtyInKg > 0 && effPrice > 0)
    ? qtyInKg * effPrice
    : (offerObj?.dealValue ?? offer?.dealValue ?? null);

  const gstRate = offerObj?.gstRate ?? offer?.gstRate ?? offerObj?.priceBreakdown?.fullOrder?.breakdown?.gstRate ?? productVarietyObj?.gst ?? 18;

  const mediaObj = offerObj?.media || offer?.media || quotationObj?.media || offer?.quotationDetails?.media;
  const rawImages = (mediaObj?.images && mediaObj.images.length > 0)
    ? mediaObj.images
    : [offerObj?.bannerImg || productVarietyObj?.img || offer?.productVariety?.img].filter(Boolean);

  const displayImages = [
    rawImages[0] || offerObj?.bannerImg || productVarietyObj?.img || null,
    rawImages[1] || rawImages[0] || offerObj?.bannerImg || productVarietyObj?.img || null,
    rawImages[2] || rawImages[0] || offerObj?.bannerImg || productVarietyObj?.img || null,
  ];

  const rawVideos = mediaObj?.videos || [];
  const displayVideos = [
    rawVideos[0] || null,
    rawVideos[1] || null,
  ];

  const refreshWallet = async () => {
    const userId = user?._id ?? user?.id;
    if (!userId) return;

    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/wallet/get-wallet-balance/${userId}`,
      );
      setCurrentWallet(data.data);
    } catch (err) {
      console.error("Failed to refresh wallet", err);
    }
  };

  const handlePay = async () => {
    if (!hasEnoughBalance) {
      setShowRecharge(true);
      return;
    }
    try {
      setPaying(true);
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/offer/pay-to-view/${offer.offer._id}`,
        { amount: PLATFORM_FEE },
      );
      if (data.success) {
        if (!data.alreadyPaid) toast.success("Platform fee paid!");
        setAnimate(false);
        setTimeout(() => onPaid(offer.offer._id), 250);
      } else {
        toast.error(data.message || "Payment failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Payment failed. Please try again.");
    } finally {
      setPaying(false);
    }
  };

  return createPortal(
    <>
      <div
        className={`fixed inset-0 z-50 flex items-end md:items-center justify-center
        bg-black/30 backdrop-blur-sm transition-opacity duration-300
        ${animate ? "opacity-100" : "opacity-0"}`}
      >
        <div
          ref={domNode}
          className={`bg-white w-full md:w-[480px] md:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col transition-all duration-300 overflow-hidden
          ${animate ? "scale-100 translate-y-0" : "scale-95 md:translate-y-4 translate-y-full"}`}
          style={{ maxHeight: "90vh" }}
        >
          {/* Modal Header */}
          <div className="px-5 py-4 bg-primaryColor text-white flex items-center rounded-t-3xl md:rounded-t-3xl">
            <button
              onClick={handleClose}
              disabled={paying}
              className="text-white text-2xl leading-none"
            >
              ←
            </button>
            <div className="flex-1 text-center font-semibold text-lg">
              Unlock Offer Details
            </div>
          </div>

          {/* Modal Content */}
          <div className="flex-1 overflow-auto p-4 space-y-3">
            <p className="text-[11px] text-gray-500 text-center leading-tight">
              Pay a small one-time platform fee to view full offer details & download media options.
            </p>

            {/* Product & Pricing Overview */}
            <div className="bg-gray-50 rounded-xl p-3 border border-gray-200/80 space-y-2">
              <div>
                <p className="text-xs font-bold text-[#3b2012] truncate">{varietyName}</p>
                {offerNumber && (
                  <span className="inline-block text-[11px] font-bold text-primaryColor tracking-wide mt-0.5">
                    {offerNumber} {gradeLabel ? <span className="text-gray-600 font-semibold">({gradeLabel})</span> : null}
                  </span>
                )}
                <p className="text-[11px] text-gray-500 mt-0.5">
                  Offer price: Rs.{typeof priceAmount === 'number' ? Math.round(priceAmount) : Math.round(Number(priceAmount) || 0)}/{priceType}
                </p>
              </div>

              {/* Total Deal Value & GST Percentage */}
              <div className="grid grid-cols-2 gap-2 pt-1.5 border-t border-gray-200/60">
                <div className="bg-white p-2 rounded-lg border border-gray-100 shadow-xs">
                  <span className="text-[9px] uppercase font-bold text-gray-400 block tracking-wider">TOTAL DEAL VALUE</span>
                  <div className="flex items-baseline gap-1 mt-0.5 flex-wrap">
                    <span className="text-xs font-extrabold text-gray-900">
                      {totalDealValue != null ? `₹${Math.round(totalDealValue).toLocaleString("en-IN")}` : "N/A"}
                    </span>
                    <span className="text-[8px] font-bold text-gray-500 uppercase tracking-tight">
                      (EXC. GST)
                    </span>
                  </div>
                </div>
                <div className="bg-white p-2 rounded-lg border border-gray-100 shadow-xs">
                  <span className="text-[9px] uppercase font-bold text-gray-400 block tracking-wider">GST RATE</span>
                  <span className="text-xs font-extrabold text-primaryColor">
                    {gstRate}% GST
                  </span>
                </div>
              </div>
            </div>

            {/* Media Section: All 5 items in ONE line (3 Photos + 2 Videos) */}
            <div className="space-y-1">
              <div className="flex items-center justify-between px-0.5">
                <span className="text-[11px] font-bold text-gray-700">Included Media</span>
                <span className="text-[9.5px] font-semibold text-gray-400">3 Photos • 2 Videos</span>
              </div>

              <div className="grid grid-cols-5 gap-1.5">
                {[
                  { title: "Prod 1", img: displayImages[0], isVideo: false },
                  { title: "Prod 2", img: displayImages[1], isVideo: false },
                  { title: "Pack", img: displayImages[2], isVideo: false },
                  { title: "P-Video", video: displayVideos[0], isVideo: true },
                  { title: "Pk-Video", video: displayVideos[1], isVideo: true },
                ].map((item, idx) => {
                  const url = item.isVideo ? item.video : item.img;
                  return (
                    <a
                      key={idx}
                      href={url || "#"}
                      target={url ? "_blank" : "_self"}
                      rel="noopener noreferrer"
                      className={`relative w-full aspect-square rounded-lg overflow-hidden border group block ${item.isVideo
                        ? "bg-gray-900 border-gray-800"
                        : "bg-gray-100 border-gray-200"
                        } ${url ? "cursor-pointer hover:opacity-90 transition-opacity" : "cursor-default"}`}
                      onClick={(e) => {
                        if (!url) e.preventDefault();
                      }}
                    >
                      {item.isVideo ? (
                        item.video ? (
                          <video src={item.video} className="w-full h-full object-cover bg-black opacity-80" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-900">
                            <svg className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        )
                      ) : item.img ? (
                        <img src={item.img} alt={item.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 bg-gray-50">
                          <svg className="w-3.5 h-3.5 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                      {item.isVideo && item.video && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none">
                          <svg className="w-3.5 h-3.5 text-white drop-shadow-xs" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      )}
                      <span className={`absolute bottom-0.5 left-0.5 right-0.5 text-center text-[7.5px] font-bold px-0.5 py-0.2 rounded truncate ${item.isVideo ? "bg-amber-500/90 text-black" : "bg-black/60 text-white"
                        }`}>
                        {item.title}
                      </span>
                    </a>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-between bg-primaryColor/5 border border-primaryColor/20 rounded-xl px-3 py-2.5">
              <span className="text-xs text-gray-600">Platform fee (one-time)</span>
              <span className="text-base font-bold text-primaryColor">Rs.{PLATFORM_FEE}</span>
            </div>

            <div className="flex items-center justify-between px-1 text-xs">
              <span className="text-gray-600">Your wallet balance</span>
              <span className={`font-bold ${hasEnoughBalance ? "text-green-600" : "text-red-500"}`}>
                Rs.{availableBalance.toLocaleString("en-IN")}
              </span>
            </div>

            {!hasEnoughBalance && (
              <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                <span className="text-xs text-red-600">Insufficient balance</span>
                <span className="text-xs font-semibold text-red-600">
                  Need Rs.{(PLATFORM_FEE - availableBalance).toLocaleString("en-IN")} more
                </span>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="p-5 border-t flex gap-3">
            <button
              onClick={handleClose}
              disabled={paying}
              className="flex-1 py-3.5 border-2 border-gray-300 text-gray-700 rounded-2xl font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handlePay}
              disabled={paying}
              className="flex-1 py-3.5 bg-primaryColor text-white rounded-2xl font-semibold disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {paying ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Processing…
                </>
              ) : hasEnoughBalance ? (
                `Pay Rs.${PLATFORM_FEE}`
              ) : (
                "Add Money"
              )}
            </button>
          </div>
        </div>
      </div>

      <RechargeModal
        isOpen={showRecharge}
        onClose={() => setShowRecharge(false)}
        wallet={currentWallet}
        user={user}
        onSuccess={refreshWallet}
      />
    </>,
    document.body,
  );
};

export default PayToViewOfferModal;